import { useState } from "react";
import {
  useCreate,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";

import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

interface ChatWidget {
  id: string;
  public_key: string;
  enabled: boolean;
  greeting: string;
  approved_info: string;
  handoff_message: string;
}

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Website chat widget configuration.
 *
 * The approved information is the whole safety model: the assistant may
 * answer from this text and nothing else, so what is written here is
 * exactly what a visitor can be told.
 */
export const ChatWidgetSettings = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [update] = useUpdate();

  const { data: widgets } = useGetList<ChatWidget>("chat_widgets", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "created_at", order: "DESC" },
  });
  const widget = (widgets ?? [])[0];

  const [greeting, setGreeting] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const createWidget = () =>
    create(
      "chat_widgets",
      { data: { workspace_id: getActiveWorkspaceId() } },
      {
        mutationMode: "pessimistic",
        onSuccess: () => refresh(),
        onError: (error: unknown) =>
          notify(
            error instanceof Error
              ? error.message
              : "Could not create the widget",
            { type: "error" },
          ),
      },
    );

  const save = (changes: Partial<ChatWidget>) => {
    if (!widget) return;
    update(
      "chat_widgets",
      { id: widget.id, data: changes, previousData: widget },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Chat widget saved");
          refresh();
        },
        onError: (error: unknown) =>
          notify(error instanceof Error ? error.message : "Could not save", {
            type: "error",
          }),
      },
    );
  };

  if (!widget) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-[12.5px] text-muted-foreground">
          A website chat widget captures leads and answers questions using only
          the information you approve.
        </p>
        <Button size="sm" className="w-fit" onClick={createWidget}>
          Set up the chat widget
        </Button>
      </div>
    );
  }

  const embed = `<iframe src="${window.location.origin}/#/w/${widget.public_key}" style="border:0;width:380px;height:560px" title="Chat"></iframe>`;

  return (
    <div className="flex flex-col gap-3">
      <label className="flex items-center gap-2 text-[13px]">
        <input
          type="checkbox"
          className="size-4 accent-primary"
          checked={widget.enabled}
          onChange={(e) => save({ enabled: e.target.checked })}
        />
        Widget is live on the website
      </label>

      <label className="flex flex-col gap-1 text-[12.5px] font-medium">
        Greeting
        <input
          className={inputClass}
          value={greeting ?? widget.greeting}
          onChange={(e) => setGreeting(e.target.value)}
          onBlur={() =>
            greeting !== null &&
            greeting !== widget.greeting &&
            save({ greeting })
          }
        />
      </label>

      <label className="flex flex-col gap-1 text-[12.5px] font-medium">
        Approved information
        <textarea
          className="min-h-40 w-full rounded-md border border-input bg-transparent px-2.5 py-1.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder={
            "Hours: Mon to Fri, 7am to 6pm.\nService area: Springfield and 30 miles around it.\nWe do: AC repair, furnace replacement, maintenance plans.\nCall out fee: $89, waived if you book the repair.\nWe do not service commercial chillers."
          }
          value={info ?? widget.approved_info}
          onChange={(e) => setInfo(e.target.value)}
          onBlur={() =>
            info !== null &&
            info !== widget.approved_info &&
            save({ approved_info: info })
          }
        />
        <span className="text-[11.5px] font-normal text-muted-foreground">
          The assistant answers from this text and nothing else. Anything not
          written here gets a "someone will follow up" reply instead of a guess,
          so prices and promises can never be invented.
        </span>
      </label>

      <div className="flex flex-col gap-1">
        <span className="text-[12.5px] font-medium">Embed code</span>
        <textarea
          readOnly
          className="min-h-16 w-full rounded-md border border-input bg-transparent px-2.5 py-1.5 font-mono text-[11.5px] outline-none"
          value={embed}
          onFocus={(e) => e.target.select()}
        />
        <span className="text-[11.5px] text-muted-foreground">
          Paste this into your website. Leads it captures appear as contacts
          with a follow-up task, tagged with the source "chat_widget", and their
          marketing consent stays "unknown" because a chat is not an opt-in.
          Replying to them one-to-one is fine; bulk campaigns skip them
          automatically until they explicitly opt in, and the campaign results
          show them as suppressed with the reason.
        </span>
      </div>
    </div>
  );
};
